import React, { Fragment } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import AssignTableColumns, {
  CohortColumns,
  OrganizationColumns,
  UsersColumns,
} from './AssignTableColumns';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import * as Constants from '@/shared/helpers/constants';
import AssignTableRow from './AssignTableRow';
import { getCadsAIUsersList, setUserFilters } from '@/containers/Cads-AI/redux/user/actions';
import { getCadsAICohortsList, setCohortFilters } from '@/containers/Cads-AI/redux/cohort/actions';
import {
  getOrganizationsList,
  setOrganizationFilters,
} from '@/containers/Cads-AI/redux/organization/actions';

const AssignTable = () => {
  const dispatch = useDispatch();
  const { assignedProduct, assignedOrgId, tenantId, assignedCohortId, assignedOrgTypeId } =
    useSelector((state) => state.cadsAI, shallowEqual);
  const { assignUsers, ...userExtras } = useSelector(
    (state) => state.cadsAIAssignmentUser,
    shallowEqual,
  );
  const { assignCohorts, ...cohortExtras } = useSelector(
    (state) => state.cadsAIAssignmentCohort,
    shallowEqual,
  );
  const { assignOrgs, ...orgExtras } = useSelector(
    (state) => state.cadsAIAssignmentOrgs,
    shallowEqual,
  );
  const { activeTab, isFromCourse, isFromLearningPath } = useSelector(
    (state) => state.cadsAIAssignPage,
    shallowEqual,
  );
  const { checkedCourse } = useSelector((state) => state.cadsAICourse, shallowEqual);
  const { checkedLearningPath } = useSelector((state) => state.cadsAILearningPaths, shallowEqual);

  const currentRecordsData = React.useMemo(() => {
    const { USERS, COHORT, ORGANIZATION } = Constants;
    switch (activeTab) {
      case USERS:
        return {
          list: assignUsers,
          ...userExtras,
          colLength: UsersColumns.length,
          setFilter: setUserFilters,
        };
      case COHORT:
        return {
          list: assignCohorts,
          ...cohortExtras,
          colLength: CohortColumns.length,
          setFilter: setCohortFilters,
        };
      case ORGANIZATION:
        return {
          list: assignOrgs,
          ...orgExtras,
          colLength: OrganizationColumns.length,
          setFilter: setOrganizationFilters,
        };
      default:
        return 0;
    }
  }, [activeTab, assignUsers, userExtras, assignCohorts, cohortExtras, assignOrgs, orgExtras]);

  const { setFilter, filters, searchAssign } = currentRecordsData;

  React.useEffect(() => {
    if (assignedOrgId && assignedProduct) fetchData(activeTab);
  }, [
    activeTab,
    filters,
    searchAssign,
    isFromCourse,
    isFromLearningPath,
    assignedOrgId,
    assignedCohortId,
    assignedProduct,
  ]);

  React.useEffect(() => {
    if (assignedOrgId && assignedProduct && activeTab === Constants.ORGANIZATION)
      fetchData(activeTab);
  }, [
    activeTab,
    filters,
    searchAssign,
    isFromCourse,
    isFromLearningPath,
    assignedOrgTypeId,
    assignedProduct,
  ]);

  const fetchData = (tab) => {
    const filter = {
      ...filters,
      ...searchAssign,
      isAssigned: false,
      feature: filters?.feature ?? assignedProduct?.id,
      organizationId: assignedOrgId,
      orgTypeId: assignedOrgTypeId,
      organizationCohortId: assignedCohortId ? assignedCohortId : null,
    };

    // check if rendering from module or learning path to add extra filters
    if (isFromCourse || isFromLearningPath) {
      if (isFromCourse) filter.courseId = checkedCourse.id;
      if (isFromLearningPath) filter.learningPathId = checkedLearningPath.id;
      filter.tenantId = tenantId;
    }

    if (tab === Constants.USERS) {
      dispatch(getCadsAIUsersList(filter));
    } else if (tab === Constants.COHORT) {
      dispatch(getCadsAICohortsList(filter));
    } else if (tab === Constants.ORGANIZATION) {
      dispatch(getOrganizationsList(filter));
    }
  };

  const handleRequestSort = (e, sortBy) => {
    dispatch(
      setFilter({ ...filters, sortBy, isSortOrderDescending: !filters.isSortOrderDescending }),
    );
  };

  const handleChangePage = (event, currentPage) => {
    dispatch(setFilter({ ...filters, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (e) => {
    dispatch(setFilter({ ...filters, pageNumber: 1, pageSize: e.target.value }));
  };

  const {
    filters: { pageNumber: page, pageSize: rowsPerPage, sortBy, isSortOrderDescending },
    totalRecords,
    colLength,
    error,
    loading,
  } = currentRecordsData;
  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage)
      ? rowsPerPage - currentRecordsData.list.length
      : 0;

  const renderTableStatus = () =>
    (loading || !!error || totalRecords === 0) && (
      <TableRow>
        <TableCell colSpan={colLength}>
          {loading && (
            <div className={'text-center overflow-hidden'}>
              <div className="panel__refresh position-relative" style={{ height: 75 }}>
                <LoadingIcon />
              </div>
            </div>
          )}
          {totalRecords === 0 && !error && !loading && (
            <div className={'text-center'}>
              <p className={'m-5'}>No records found with applied filter.</p>
            </div>
          )}
          {(!!error || loading) && (
            <div className={'text-center'}>
              <p className={!!error ? 'm-5 error' : ''}>{error || 'Loading...'}</p>
            </div>
          )}
        </TableCell>
      </TableRow>
    );

  return (
    <Fragment>
      <div className="material-table__wrap">
        <Table className="material-table">
          <TableBody>
            <AssignTableColumns
              order={isSortOrderDescending ? 'desc' : 'asc'}
              orderBy={sortBy}
              onRequestSort={handleRequestSort}
              rowCount={rowsPerPage}
            />

            {renderTableStatus()}
            <AssignTableRow data={currentRecordsData.list} orgData={currentRecordsData.data} />

            {/*Rendering empty rows for better UI*/}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={colLength} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default AssignTable;
